import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Card from "components/Card";
import Text from "components/Text";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { officePolicyAssignmentStatus } from "constants";
import styles from "../../OfficePolicyManagement.module.scss";
import Exlamatory_Icon from "assets/images/red_exclamatory_icon.svg";
import CommentIcon from "assets/images/comment_icon.svg";
import checked_policy_acknowledged from "assets/images/checked_policy_acknowledged.svg";
const dotsIcon = require("assets/images/dots-icon.svg").default;

const { PENDING, ACCEPTED, STARTED, COMPLETED, CANCELLED } =
  officePolicyAssignmentStatus;

const INCOMPLETE_STATUSES = new Set([PENDING, ACCEPTED, STARTED]);

/** Show "The policy is Overdue" when API says so, or when due date is past and status is incomplete. */
function shouldShowOverdue(assignee) {
  if (typeof assignee?.isOverdue === "boolean") return assignee.isOverdue;
  if (!assignee?.dueDate) return false;
  const due = moment.utc(assignee.dueDate);
  if (!due.isBefore(moment.utc())) return false;
  return INCOMPLETE_STATUSES.has(assignee?.status);
}

export default function AssigneeCard({
  assignee,
  getButtonClass,
  onCardClick,
  onCancelClick,
  onReassign,
}) {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggle = () => setDropdownOpen((prev) => !prev);

  const statusLabel = assignee?.statusLabel ?? "";
  const isPending = assignee?.status === PENDING;
  const isCancelledNotReassigned =
    assignee?.status === CANCELLED && !assignee?.isReassigned;
  const isCompleted = assignee?.status === COMPLETED;
  /** Show ellipsis only for Pending (Cancel) or Cancelled (Reassign); no kebab for Rejected or Completed */
  const showActions =
    (isPending || isCancelledNotReassigned) && !assignee?.isReassigned;

  const handleCardClick = (e) => {
    if (e.target.closest(".interactive-element")) return;
    onCardClick?.(e, assignee);
  };

  return (
    <Card
      className={styles.task_details_card_wrapper}
      onClick={handleCardClick}
      style={{ cursor: "pointer" }}
    >
      {shouldShowOverdue(assignee) && (
        <div className={styles.overdue_warning_wrap}>
          <img src={Exlamatory_Icon} alt="" className="mr-2" />
          <p className={`${styles.overdue_warning} m-0`}>
            {t("officePolicyManagement.policyOverdue")}
          </p>
        </div>
      )}
      <div className={styles.btn_dropdown_wrapper}>
        <div className={styles.status_priority_date_wrapper}>
          <button type="button" className={getButtonClass(statusLabel)}>
            {(statusLabel || "—").toUpperCase()}
          </button>
        </div>
        {showActions && (
          <div
            className="interactive-element"
            onClick={(e) => e.stopPropagation()}
          >
            <Dropdown isOpen={dropdownOpen} toggle={toggle}>
              <DropdownToggle
                caret={false}
                tag="div"
                className="dropdown-btn px-3"
                style={{ cursor: "pointer", minWidth: 24, minHeight: 24 }}
              >
                {dotsIcon ? (
                  <img src={dotsIcon} alt="actions" />
                ) : (
                  <span aria-hidden style={{ fontSize: 18, lineHeight: 1 }}>
                    ⋮
                  </span>
                )}
              </DropdownToggle>
              <DropdownMenu end className={styles.custom_dropdown_wrapper}>
                {/* Backup: Pending only -> Cancel (opens cancel modal; assignee.id = assignment id) */}
                {isPending && (
                  <DropdownItem
                    tag="div"
                    className={`${styles.dropdown_list} p-0`}
                  >
                    <button
                      type="button"
                      className={`${styles.dropdown_link} mb-0 border-0 bg-transparent w-100 text-left`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(false);
                        onCancelClick?.(assignee?.assignmentId ?? assignee?.id);
                      }}
                    >
                      {t("officePolicyManagement.cancel")}
                    </button>
                  </DropdownItem>
                )}
                {/* Cancelled (and !isReassigned) -> Reassign Policy */}
                {isCancelledNotReassigned && (
                  <DropdownItem
                    tag="div"
                    className={`${styles.dropdown_list} p-0`}
                  >
                    <button
                      type="button"
                      className={`${styles.dropdown_link} border-0 bg-transparent w-100 text-left`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(false);
                        onReassign?.(assignee);
                      }}
                    >
                      {t("officePolicyManagement.reassignPolicy")}
                    </button>
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
      </div>
      <p className={styles.card_main_heading}>{assignee?.name ?? "—"}</p>
      <div className={styles.dental_name_comment}>
        <div>
          <Text size="12px" weight="400" color="#6F7788">
            {assignee?.officeName ?? "—"}
          </Text>
        </div>
        <div className="d-flex align-items-center gap-3">
          <img
            style={{ display: "inline-block" }}
            src={CommentIcon}
            alt=""
            className="mr-1"
          />
          <Text size="12px" weight="400" color="#6F7788">
            {assignee?.totalComments ?? 0}{" "}
            {t("officePolicyManagement.comments") || "Comments"}
          </Text>
        </div>
      </div>

      {isCompleted && (
        <Text
          backgroundColor="#FCC539"
          padding="8px 10px"
          size="10px"
          weight="500"
          color="#2D3245"
          marginTop="8px"
        >
          <img src={checked_policy_acknowledged} alt="" />{" "}
          {t("officePolicyManagement.policyAcknowledgeByStaff")}
        </Text>
      )}
    </Card>
  );
}
